import { Link } from "react-router-dom";
import { Calendar, MessageCircle, User } from "lucide-react";
import type { Locale } from "@/context/AppProviders";
import { blogCardImageUrl } from "@/lib/cdnImage";
import type { BlogListItem } from "@/types/blog";

type BlogRelatedPostCardProps = {
  post: BlogListItem;
  href: string;
  locale: Locale;
};

export function BlogRelatedPostCard({ post, href, locale }: BlogRelatedPostCardProps) {
  const isTr = locale === "tr";

  return (
    <article className="blog-related-card shrink-0 snap-start">
      <Link
        to={href}
        className="group flex h-full flex-col overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] no-underline transition duration-300 hover:border-enabase-blue/25 hover:shadow-[0_12px_40px_-24px_rgba(37,105,195,0.35)]"
      >
        {post.imageUrl ? (
          <div className="aspect-[16/10] overflow-hidden bg-[var(--bg3)]">
            <img
              src={blogCardImageUrl(post.imageUrl)}
              alt=""
              aria-hidden
              width={640}
              height={400}
              sizes="(max-width: 640px) 85vw, 320px"
              className="size-full object-cover transition duration-500 group-hover:scale-[1.03]"
              loading="lazy"
              decoding="async"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-linear-to-br from-enabase-blue/15 to-[var(--bg3)]" aria-hidden />
        )}

        <div className="flex flex-1 flex-col p-5">
          <h3 className="line-clamp-2 text-base font-bold leading-snug tracking-tight text-[var(--text)] sm:text-[1.0625rem]">
            {post.title}
          </h3>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[var(--text-md)]">
            {post.dateTime || post.date ? (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="size-3.5 shrink-0 opacity-70" aria-hidden />
                <time>{post.dateTime || post.date}</time>
              </span>
            ) : null}
            {post.author ? (
              <span className="inline-flex items-center gap-1.5">
                <User className="size-3.5 shrink-0 opacity-70" aria-hidden />
                <span>{post.author}</span>
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5">
              <MessageCircle className="size-3.5 shrink-0 opacity-70" aria-hidden />
              <span>
                {isTr
                  ? `${post.commentCount} yorum`
                  : `${post.commentCount} comment${post.commentCount === 1 ? "" : "s"}`}
              </span>
            </span>
          </div>

          {post.excerpt ? (
            <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[var(--text-md)]">{post.excerpt}</p>
          ) : null}
        </div>
      </Link>
    </article>
  );
}
